import React, { memo, useEffect, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, Resolver, useForm } from 'react-hook-form'
import { Tooltip } from 'primereact/tooltip'
import { Button } from 'primereact/button'

import { Input } from '../../../../../common/uikit/Input'
import { Dropdown } from '../../../../../common/uikit/Dropdown'
import { Switch } from '../../../../../common/uikit/Switch'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { useToast } from '../../../../../common/uikit/ToastProvider'
import {
    EarthingSystem,
    LoadBalancerGroup,
    useCompanyGetDeviceByCompanyIdQuery,
    useLoadBalancerGroupCreateMutation,
    CompanyPreview,
} from '../../../../../common/redux/api/web-api'
import { useLoadbalancerSchema } from '../../../../../common/features/loadbalancer-group/load-balancer-group-form-validation'
import { transformLBCurrentInput } from '../../../../../common/utils/load-balancer-current-input-transform'
import { LoadbalancerPhaseSettingsWidget } from '../../../../../common/components/loadbalancer-phase-settings-widget'
import { EarthingSchemeLB } from '../../../../../common/enums/earthingSchemes'
import { Phases } from '../../../../../common/domain/electrical-settings'
import useText from '../../../../../common/hooks/useText'

export type CreateLoadBalancerGroupDialogProps = {
    className?: ClassName
    visible: boolean
    onHide: () => void
    organisations: CompanyPreview[]
    group: LoadBalancerGroup
}

export const CreateLoadBalancerGroupDialog = memo(function CreateLoadBalancerGroupDialog({
    className,
    visible,
    onHide,
    organisations,
    group,
}: CreateLoadBalancerGroupDialogProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const [earthingScheme, setEarthingScheme] = useState<EarthingSystem>(EarthingSchemeLB.TN)

    const [allowedPhases, setAllowedPhases] = useState<string | null | undefined>(Phases.L1L2L3)
    const [measurementDevice, setMeasurementDevice] = useState(group?.measurementDeviceId)
    const [schukoEnabled, setSchukoEnabled] = useState<boolean>(group?.schukoEnabled ?? false)
    const [phasesValid, setPhasesValid] = useState<boolean>(false)
    const [chargePointsIncluded, setChargePointsIncluded] = useState<boolean>(
        group?.chargePointsIncludedInMeasurement ?? true
    )

    const [createLoadBalancerGroup, { isLoading }] = useLoadBalancerGroupCreateMutation()

    const loadbalancerSchema = useLoadbalancerSchema()

    const {
        register,
        trigger,
        watch,
        handleSubmit,
        control,
        reset,
        formState: { errors, isSubmitted, isValid },
    } = useForm<LoadBalancerGroup>({
        resolver: yupResolver(loadbalancerSchema(measurementDevice ? true : false), {
            abortEarly: false,
        }) as unknown as Resolver<LoadBalancerGroup>,
        defaultValues: group,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })
    const minCurrent = watch('minCurrentPerChargePoint')
    const companyId = watch('companyId') ?? ''
    const { data: deviceData } = useCompanyGetDeviceByCompanyIdQuery({ companyId }, { skip: !companyId })

    useEffect(() => {
        if (minCurrent && isSubmitted) {
            trigger('maxCurrentPerPhase')
            trigger('deviceControlledMaxCurrentPerPhase')
        }
    }, [isSubmitted, minCurrent, trigger])

    useEffect(() => {
        if (!visible) return

        reset(group)
        setEarthingScheme(group.earthingSystem ?? EarthingSchemeLB.TN)
        setAllowedPhases(group.allowedPhases ?? Phases.L1L2L3)
        setMeasurementDevice(group.measurementDeviceId)
        setSchukoEnabled(group.schukoEnabled ?? false)
        setPhasesValid(false)
        setChargePointsIncluded(group.chargePointsIncludedInMeasurement ?? true)
    }, [group, reset, visible])

    const doAddLBGroup = async (formData: LoadBalancerGroup) => {
        if (!formData.companyId || !phasesValid) return

        const tempLoadBalancerGroup = {
            ...group,
            ...formData,
            name: formData?.name?.trim(),
            allowedPhases: allowedPhases,
            schukoEnabled: schukoEnabled,
            earthingSystem: earthingScheme,
            chargePointsIncludedInMeasurement: chargePointsIncluded,
        }
        try {
            await createLoadBalancerGroup({ loadBalancerGroup: tempLoadBalancerGroup }).unwrap()
            onHide()
            toast.notifySuccess(text('msgLoadbalancerGroupAdded'))
        } catch (error) {
            onHide()
            toast.notifyError(text('msgLoadbalancerGroupAddedError'))
        }
    }

    const disabledCreateButton = isLoading || (isSubmitted && (!isValid || !phasesValid))

    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} disabled={isLoading} />
            <Button
                label={text('btnCreate')}
                type="submit"
                form="load-balancer-group-form"
                disabled={disabledCreateButton}
                loading={isLoading}
            />
        </div>
    )

    return (
        <Dialog
            header={text('lblNewLoadbalancerGroupModal')}
            visible={visible}
            className={cn(className)}
            onHide={onHide}
            footer={dialogFooter}
        >
            <form
                id="load-balancer-group-form"
                name="form"
                role="form"
                noValidate
                className="flex flex-column gap-2 pt-3"
                onSubmit={handleSubmit(doAddLBGroup)}
            >
                <div>
                    <Input
                        id="name"
                        label={text('lblName')}
                        required
                        data-testid="name-input"
                        {...register('name')}
                        error={errors.name?.message}
                    />
                </div>
                <div>
                    <Input
                        {...register('description')}
                        id="description"
                        label={text('lblDescription')}
                        data-testid="description-input"
                    />
                </div>

                <div>
                    <Controller
                        name="companyId"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Dropdown
                                id="companyId"
                                required
                                label={text('lblOrganisation')}
                                options={organisations?.map((organisation) => ({
                                    value: organisation?.id,
                                    label: organisation?.name || '',
                                }))}
                                value={value ?? null}
                                onChange={(e) => onChange(e.value)}
                                error={errors.companyId?.message}
                                data-testid="company-input"
                                virtualScrollerOptions={{ itemSize: 38 }}
                                showClear={false}
                            />
                        )}
                    />
                </div>
                <div>
                    <Controller
                        name="measurementDeviceId"
                        control={control}
                        render={({ field: { onChange } }) => (
                            <Dropdown
                                id="measurementDevice"
                                data-testid="device-input"
                                label={text('lblMeasurementDevice')}
                                options={deviceData
                                    ?.filter((device) => !device?.loadBalancerGroupId)
                                    .map((device) => ({
                                        value: device?.id,
                                        label: device?.name || device?.serialNumber,
                                    }))}
                                value={measurementDevice}
                                placeholder={
                                    deviceData && deviceData.length > 0
                                        ? text('lblSelect')
                                        : text('lblmeasurementdeviceNotAvailable')
                                }
                                onChange={(e) => {
                                    onChange(e.value)
                                    setMeasurementDevice(e.value)
                                }}
                                disabled={!companyId}
                                showClear
                            />
                        )}
                    />
                </div>
                <div>
                    <LoadbalancerPhaseSettingsWidget
                        earthingScheme={earthingScheme}
                        setEarthingScheme={setEarthingScheme}
                        loadbalancerGroup={group}
                        setAllowedPhases={setAllowedPhases}
                        setPhasesValid={setPhasesValid}
                        classNameComponent="flex gap-5 md:flex-nowrap"
                        classNameSubHeading="gap-3"
                    />
                </div>

                <div className="flex justify-content-between gap-4 mb-4 lg:flex-row flex-column">
                    <div className="w-full lg:w-6">
                        <Controller
                            name="minCurrentPerChargePoint"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input
                                    required
                                    id="minCurrentPerCharger"
                                    label={text('lblMinCurrentPerCharger')}
                                    data-testid="MinCurrentPerCharger-input"
                                    error={errors.minCurrentPerChargePoint?.message}
                                    type="number"
                                    onChange={(e) => {
                                        const transformedValue = parseInt(e.target.value)
                                        onChange(transformLBCurrentInput.input(transformedValue))
                                    }}
                                    value={value?.toString() ?? ''}
                                />
                            )}
                        />
                    </div>
                    <div className="w-full lg:w-6">
                        <Controller
                            name="maxCurrentPerPhase"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input
                                    id="installationWizardMaxCurrentPerPhase"
                                    label={
                                        measurementDevice
                                            ? text('lblMaxCurrentPerPhaseAGOffline')
                                            : text('lblInstallationWizardMaxCurrentPerPhase')
                                    }
                                    required
                                    data-testid="installationWizardMaxCurrentPerPhase-input"
                                    error={errors.maxCurrentPerPhase?.message}
                                    type="number"
                                    onChange={(e) => {
                                        const transformedValue = parseInt(e.target.value)
                                        onChange(transformLBCurrentInput.input(transformedValue))
                                    }}
                                    value={value?.toString() ?? ''}
                                />
                            )}
                        />
                    </div>
                </div>
                {measurementDevice && (
                    <div className="flex justify-content-between gap-4 mb-4 lg:flex-row flex-column">
                        <div className="w-full lg:w-6">
                            <Controller
                                name="deviceControlledMaxCurrentPerPhase"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Input
                                        id="maxCurrentPerPhaseAGOnline"
                                        label={text('lblMaxCurrentPerPhaseAGOnline')}
                                        required
                                        data-testid="maxCurrentPerPhaseAGGOnline-input"
                                        error={errors.deviceControlledMaxCurrentPerPhase?.message}
                                        type="number"
                                        onChange={(e) => {
                                            const transformedValue = parseInt(e.target.value)
                                            onChange(transformLBCurrentInput.input(transformedValue))
                                        }}
                                        value={value?.toString() ?? ''}
                                    />
                                )}
                            />
                        </div>
                        <div className="w-full lg:w-6">
                            <Controller
                                name="supplyMaxCurrent"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Input
                                        id="supplyMaxCurrentMeasurementDeviceField"
                                        label={text('lblSupplyMaxCurrent')}
                                        required
                                        data-testid="supplyMaxCurrentMeasurementDeviceField-input"
                                        error={errors.supplyMaxCurrent?.message}
                                        type="number"
                                        onChange={(e) => {
                                            const transformedValue = parseInt(e.target.value)
                                            onChange(transformLBCurrentInput.input(transformedValue))
                                        }}
                                        value={value?.toString() ?? ''}
                                    />
                                )}
                            />
                        </div>
                    </div>
                )}
                <div className="flex justify-content-between gap-4 mb-4 lg:flex-row flex-column">
                    <div className="flex gap-2">
                        <Controller
                            name="schukoEnabled"
                            control={control}
                            render={({ field: { onChange } }) => (
                                <div className="flex align-items-center gap-2">
                                    <Switch
                                        inputId="schukoEnabled"
                                        data-testid="socket-enabled-toggle"
                                        checked={schukoEnabled}
                                        label={text('lblSocketEnabled')}
                                        onChange={(event) => {
                                            const newValue = !!event.value
                                            setSchukoEnabled(newValue)
                                            onChange(newValue)
                                        }}
                                    />
                                </div>
                            )}
                        />
                    </div>
                </div>

                {measurementDevice && (
                    <div className="flex gap-2">
                        <Controller
                            name="chargePointsIncludedInMeasurement"
                            control={control}
                            render={({ field: { onChange } }) => (
                                <div className="flex align-items-center gap-2">
                                    <Switch
                                        inputId="chargePointsIncludedInMeasurement"
                                        data-testid="chargepoints-included-toggle"
                                        checked={chargePointsIncluded}
                                        label={text('lblChargersIncludedInPowerConsumption')}
                                        onChange={(event) => {
                                            const newValue = !!event.value
                                            setChargePointsIncluded(newValue)
                                            onChange(newValue)
                                        }}
                                    />
                                </div>
                            )}
                        />

                        <span className="flex align-items-center">
                            <i
                                id="chargersincluded-info-icon"
                                className="pi pi-info-circle text-primary"
                                data-pr-tooltip={text('lblChargersIncludedInPowerConsumptionInfo')}
                                data-pr-position="top"
                            />
                            <Tooltip target="#chargersincluded-info-icon" />
                        </span>
                    </div>
                )}
            </form>
        </Dialog>
    )
})

export default CreateLoadBalancerGroupDialog
